
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { ReferralURLConfig } from '@/utils/config/types';

const urlSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  url: z.string().url({ message: "Please enter a valid URL" }),
  isDefault: z.boolean().default(false),
});

interface ReferralURLAddEditFormProps {
  editingUrl?: ReferralURLConfig;
  onSubmit: (data: z.infer<typeof urlSchema>) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  maxUrlsReached?: boolean;
}

const ReferralURLAddEditForm: React.FC<ReferralURLAddEditFormProps> = ({
  editingUrl,
  onSubmit,
  onCancel,
  isSubmitting,
  maxUrlsReached
}) => {
  const form = useForm<z.infer<typeof urlSchema>>({
    resolver: zodResolver(urlSchema),
    defaultValues: {
      name: editingUrl?.name || '',
      url: editingUrl?.baseUrl || '',
      isDefault: editingUrl?.isDefault || false,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Main Website" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Set as Default</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Make this the default URL for referrals
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={editingUrl?.isDefault === true}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || maxUrlsReached}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save URL'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ReferralURLAddEditForm;
